import {
  type EveryMinutes,
  type Segment,
  SegmentSchema,
  type SyncRule,
  type SyncSchedule,
  type Weekday,
  nextRuns,
} from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
/**
 * Admin-Card: Zeitplan für den automatischen Salesforce-Sync (Regel-Builder).
 * Schreibt SyncSchedule via PUT /v1/sync-runs/schedule; der Worker liest ihn und
 * wertet die Regeln per Minuten-Tick aus (packages/shared sync-schedule/evaluate).
 * Mehrere Regeln gelten additiv. Vorschau "Nächste Läufe" via nextRuns().
 * Segment-Whitelist: NULL/leer = alle Segmente (Bestandsschutz).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { useToast } from './toast.js';

/** Alle Segmentwerte aus dem kanonischen Enum — keine Hardcodes. */
const ALL_SEGMENTS: Segment[] = SegmentSchema.options;

const EVERY_MINUTES: EveryMinutes[] = [10, 20, 30, 45, 60, 90, 120];
const FIELD_CLASS = 'border rounded px-2 py-1 text-sm';
const fieldStyle = {
  borderColor: 'var(--line-soft)',
  background: 'var(--surface-2)',
  color: 'var(--ink)',
} as const;

function defaultRule(type: SyncRule['type']): SyncRule {
  switch (type) {
    case 'daily_at':
      return { type: 'daily_at', times: ['07:00'] };
    case 'interval_window':
      return { type: 'interval_window', from: '08:00', to: '18:00', everyMinutes: 30 };
    case 'every_n_hours':
      return { type: 'every_n_hours', n: 3 };
    case 'weekday_at':
      return { type: 'weekday_at', entries: [{ weekday: 1, time: '10:00' }] };
  }
}

export function SyncScheduleCard() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const weekdayShort = [
    t('admin.sched.wd_su', 'So'),
    t('admin.sched.wd_mo', 'Mo'),
    t('admin.sched.wd_tu', 'Di'),
    t('admin.sched.wd_we', 'Mi'),
    t('admin.sched.wd_th', 'Do'),
    t('admin.sched.wd_fr', 'Fr'),
    t('admin.sched.wd_sa', 'Sa'),
  ];

  const scheduleQuery = useQuery({
    queryKey: ['sync-schedule'],
    queryFn: () => api.getSyncSchedule(),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const [draft, setDraft] = useState<SyncSchedule | null>(null);
  useEffect(() => {
    if (scheduleQuery.data) {
      setDraft({
        enabled: scheduleQuery.data.enabled,
        rules: scheduleQuery.data.rules,
        segment_whitelist: scheduleQuery.data.segment_whitelist,
        refresh_enabled: scheduleQuery.data.refresh_enabled,
        refresh_rules: scheduleQuery.data.refresh_rules,
      });
    }
  }, [scheduleQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (schedule: SyncSchedule) => api.putSyncSchedule(schedule),
    onSuccess: (res) => {
      queryClient.setQueryData(['sync-schedule'], res);
      toast.success(t('admin.sched.saved', 'Zeitplan gespeichert'));
    },
    onError: (err) =>
      toast.error(
        t('admin.sched.save_failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const [watermarkDate, setWatermarkDate] = useState('');
  const watermarkMutation = useMutation({
    mutationFn: (value: string | null) => api.setSyncWatermark(value),
    onSuccess: (res) => {
      queryClient.setQueryData(['sync-schedule'], res);
      toast.success(t('admin.sched.watermark_saved', 'Delta-Wasserzeichen aktualisiert'));
    },
    onError: (err) =>
      toast.error(
        t('admin.sched.watermark_failed', 'Wasserzeichen fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const cardStyle = {
    borderColor: 'var(--line-soft)',
    boxShadow: 'var(--shadow-card)',
  } as const;

  if (!draft) {
    return (
      <div className="bg-[var(--surface)] rounded-xl border p-4" style={cardStyle}>
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('admin.sched.loading', 'Zeitplan wird geladen…')}
        </p>
      </div>
    );
  }

  const baseline = scheduleQuery.data
    ? {
        enabled: scheduleQuery.data.enabled,
        rules: scheduleQuery.data.rules,
        segment_whitelist: scheduleQuery.data.segment_whitelist,
        refresh_enabled: scheduleQuery.data.refresh_enabled,
        refresh_rules: scheduleQuery.data.refresh_rules,
      }
    : null;
  const dirty = baseline ? JSON.stringify(draft) !== JSON.stringify(baseline) : false;

  const updateRule = (idx: number, rule: SyncRule) =>
    setDraft({ ...draft, rules: draft.rules.map((r, i) => (i === idx ? rule : r)) });
  const removeRule = (idx: number) =>
    setDraft({ ...draft, rules: draft.rules.filter((_, i) => i !== idx) });
  const addRule = (type: SyncRule['type']) =>
    setDraft({ ...draft, rules: [...draft.rules, defaultRule(type)] });

  const upcoming = draft.enabled ? nextRuns(draft.rules, new Date(), 5) : [];

  const updateRefreshRule = (idx: number, rule: SyncRule) =>
    setDraft({
      ...draft,
      refresh_rules: draft.refresh_rules.map((r, i) => (i === idx ? rule : r)),
    });
  const removeRefreshRule = (idx: number) =>
    setDraft({ ...draft, refresh_rules: draft.refresh_rules.filter((_, i) => i !== idx) });
  const addRefreshRule = (type: SyncRule['type']) =>
    setDraft({ ...draft, refresh_rules: [...draft.refresh_rules, defaultRule(type)] });
  const upcomingRefresh = draft.refresh_enabled ? nextRuns(draft.refresh_rules, new Date(), 5) : [];

  return (
    <section className="bg-[var(--surface)] rounded-xl border p-4 space-y-4" style={cardStyle}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {t('admin.sched.title', 'Automatischer Sync')}
        </h3>
        <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--ink-2)' }}>
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          />
          {draft.enabled ? t('admin.sched.enabled', 'aktiv') : t('admin.sched.disabled', 'inaktiv')}
        </label>
      </div>

      <p className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
        {t(
          'admin.sched.hint',
          'Regeln gelten additiv (ODER). Der Worker lädt nur seit dem letzten Lauf neu hinzugekommene Projekte.',
        )}
      </p>

      {/* Segment-Whitelist: NULL/leer = alle Segmente (Bestandsschutz) */}
      <SegmentWhitelistEditor
        value={draft.segment_whitelist ?? null}
        onChange={(whitelist) => setDraft({ ...draft, segment_whitelist: whitelist ?? undefined })}
      />

      <div className="space-y-2">
        {draft.rules.length === 0 && (
          <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
            {t('admin.sched.no_rules', 'Noch keine Regeln. Unten eine hinzufügen.')}
          </p>
        )}
        {draft.rules.map((rule, idx) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: Regeln sind positionsstabil in der Liste
            key={idx}
            className="rounded-lg border p-3 flex items-start gap-3"
            style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
          >
            <div className="flex-1 min-w-0">
              <RuleEditor
                rule={rule}
                weekdayShort={weekdayShort}
                onChange={(r) => updateRule(idx, r)}
              />
            </div>
            <button
              type="button"
              onClick={() => removeRule(idx)}
              className="text-xs px-2 py-1 rounded border shrink-0"
              style={{ borderColor: 'var(--line-soft)', color: 'var(--danger)' }}
            >
              {t('admin.sched.remove', 'Entfernen')}
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <AddButton
          label={t('admin.sched.add_daily', '+ Täglich um …')}
          onClick={() => addRule('daily_at')}
        />
        <AddButton
          label={t('admin.sched.add_window', '+ Zeitfenster …')}
          onClick={() => addRule('interval_window')}
        />
        <AddButton
          label={t('admin.sched.add_hours', '+ Alle N Stunden …')}
          onClick={() => addRule('every_n_hours')}
        />
        <AddButton
          label={t('admin.sched.add_weekday', '+ Wochentag-Zeiten …')}
          onClick={() => addRule('weekday_at')}
        />
      </div>

      {draft.enabled && (
        <div
          className="text-xs rounded-lg p-2"
          style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
        >
          <span style={{ color: 'var(--ink-3)' }}>
            {t('admin.sched.next_runs', 'Nächste Läufe:')}
          </span>{' '}
          {upcoming.length === 0
            ? t('admin.sched.next_none', '—')
            : upcoming
                .map((d) =>
                  d.toLocaleString(i18n.language, {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
                )
                .join(' · ')}
        </div>
      )}

      <div
        className="rounded-lg border p-3 space-y-2"
        style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
      >
        <div className="text-[11px] font-medium" style={{ color: 'var(--ink-2)' }}>
          {t('admin.sched.watermark_title', 'Delta-Wasserzeichen')}
        </div>
        <div className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
          {t('admin.sched.watermark_current', 'Aktuell:')}{' '}
          {scheduleQuery.data?.delta_watermark
            ? new Date(scheduleQuery.data.delta_watermark).toLocaleString(i18n.language)
            : t('admin.sched.watermark_none', 'nicht gesetzt → nächster Lauf zieht voll')}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={watermarkDate}
            onChange={(e) => setWatermarkDate(e.target.value)}
            className={FIELD_CLASS}
            style={fieldStyle}
          />
          <button
            type="button"
            onClick={() => watermarkMutation.mutate(watermarkDate || null)}
            disabled={!watermarkDate || watermarkMutation.isPending}
            className="text-xs px-2.5 py-1 rounded border disabled:opacity-50"
            style={{ borderColor: 'var(--line-soft)', color: 'var(--ink-2)' }}
          >
            {t('admin.sched.watermark_set', 'Ab Datum setzen')}
          </button>
          <button
            type="button"
            onClick={() => watermarkMutation.mutate(null)}
            disabled={watermarkMutation.isPending}
            className="text-xs px-2.5 py-1 rounded border disabled:opacity-50"
            style={{ borderColor: 'var(--line-soft)', color: 'var(--ink-3)' }}
          >
            {t('admin.sched.watermark_clear', 'Löschen (Voll-Lauf)')}
          </button>
        </div>
        <p className="text-[10.5px]" style={{ color: 'var(--ink-3)' }}>
          {t(
            'admin.sched.watermark_hint',
            'Ab welchem CreatedDate der nächste Lauf zieht. Rückt nach jedem erfolgreichen Lauf automatisch vor.',
          )}
        </p>
      </div>

      <div className="pt-2 border-t" style={{ borderColor: 'var(--line-soft)' }}>
        <div className="flex items-center justify-between gap-3 mb-2">
          <h3 className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
            {t('admin.sched.refresh_title', 'Re-Sync bestehender Projekte')}
          </h3>
          <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--ink-2)' }}>
            <input
              type="checkbox"
              checked={draft.refresh_enabled}
              onChange={(e) => setDraft({ ...draft, refresh_enabled: e.target.checked })}
            />
            {draft.refresh_enabled
              ? t('admin.sched.enabled', 'aktiv')
              : t('admin.sched.disabled', 'inaktiv')}
          </label>
        </div>

        <p className="text-[11px] mb-2" style={{ color: 'var(--ink-3)' }}>
          {t(
            'admin.sched.refresh_hint',
            'Aktualisiert alle offenen Projekte aus Salesforce (nur Salesforce-Felder, keine operativen Daten). Empfehlung: täglich nachts.',
          )}
        </p>

        <div className="space-y-2">
          {draft.refresh_rules.length === 0 && (
            <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
              {t('admin.sched.no_rules', 'Noch keine Regeln. Unten eine hinzufügen.')}
            </p>
          )}
          {draft.refresh_rules.map((rule, idx) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: Regeln sind positionsstabil in der Liste
              key={idx}
              className="rounded-lg border p-3 flex items-start gap-3"
              style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
            >
              <div className="flex-1 min-w-0">
                <RuleEditor
                  rule={rule}
                  weekdayShort={weekdayShort}
                  onChange={(r) => updateRefreshRule(idx, r)}
                />
              </div>
              <button
                type="button"
                onClick={() => removeRefreshRule(idx)}
                className="text-xs px-2 py-1 rounded border shrink-0"
                style={{ borderColor: 'var(--line-soft)', color: 'var(--danger)' }}
              >
                {t('admin.sched.remove', 'Entfernen')}
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-2">
          <AddButton
            label={t('admin.sched.add_daily', '+ Täglich um …')}
            onClick={() => addRefreshRule('daily_at')}
          />
          <AddButton
            label={t('admin.sched.add_window', '+ Zeitfenster …')}
            onClick={() => addRefreshRule('interval_window')}
          />
          <AddButton
            label={t('admin.sched.add_hours', '+ Alle N Stunden …')}
            onClick={() => addRefreshRule('every_n_hours')}
          />
          <AddButton
            label={t('admin.sched.add_weekday', '+ Wochentag-Zeiten …')}
            onClick={() => addRefreshRule('weekday_at')}
          />
        </div>

        {draft.refresh_enabled && (
          <div
            className="text-xs rounded-lg p-2 mt-2"
            style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
          >
            <span style={{ color: 'var(--ink-3)' }}>
              {t('admin.sched.next_runs', 'Nächste Läufe:')}
            </span>{' '}
            {upcomingRefresh.length === 0
              ? t('admin.sched.next_none', '—')
              : upcomingRefresh
                  .map((d) =>
                    d.toLocaleString(i18n.language, {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  )
                  .join(' · ')}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => saveMutation.mutate(draft)}
          disabled={!dirty || saveMutation.isPending}
          className="px-3 py-1 rounded bg-[var(--primary)] text-[color:var(--on-primary)] text-sm disabled:opacity-50"
        >
          {saveMutation.isPending
            ? t('admin.sched.saving', 'speichert…')
            : t('admin.sched.save', 'Speichern')}
        </button>
        {dirty && baseline && (
          <button
            type="button"
            onClick={() => setDraft(baseline)}
            className="text-xs px-2 py-1 rounded border"
            style={{ borderColor: 'var(--line-soft)', color: 'var(--ink-2)' }}
          >
            {t('admin.sched.reset', 'Verwerfen')}
          </button>
        )}
        {dirty && (
          <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
            {t('admin.sched.unsaved', 'ungespeicherte Änderungen')}
          </span>
        )}
      </div>
    </section>
  );
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs px-2.5 py-1 rounded border"
      style={{
        borderColor: 'var(--line-soft)',
        color: 'var(--ink-2)',
        background: 'var(--surface)',
      }}
    >
      {label}
    </button>
  );
}

function RuleEditor({
  rule,
  weekdayShort,
  onChange,
}: {
  rule: SyncRule;
  weekdayShort: string[];
  onChange: (rule: SyncRule) => void;
}) {
  const { t } = useTranslation();

  if (rule.type === 'daily_at') {
    return (
      <div className="space-y-1.5">
        <div className="text-[11px] font-medium" style={{ color: 'var(--ink-2)' }}>
          {t('admin.sched.daily_label', 'Täglich um')}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {rule.times.map((time, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: editierbare Liste, Position stabil
            <span key={`${time}-${i}`} className="flex items-center gap-1">
              <input
                type="time"
                value={time}
                className={FIELD_CLASS}
                style={fieldStyle}
                onChange={(e) =>
                  onChange({
                    ...rule,
                    times: rule.times.map((x, j) => (j === i ? e.target.value : x)),
                  })
                }
              />
              {rule.times.length > 1 && (
                <button
                  type="button"
                  onClick={() => onChange({ ...rule, times: rule.times.filter((_, j) => j !== i) })}
                  style={{ color: 'var(--ink-3)' }}
                  title={t('admin.sched.remove_time', 'Zeit entfernen')}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          <button
            type="button"
            onClick={() => onChange({ ...rule, times: [...rule.times, '12:00'] })}
            className="text-xs px-2 py-1 rounded border"
            style={{ borderColor: 'var(--line-soft)', color: 'var(--ink-2)' }}
          >
            {t('admin.sched.add_time', '+ Zeit')}
          </button>
        </div>
      </div>
    );
  }

  if (rule.type === 'interval_window') {
    return (
      <div className="space-y-1.5">
        <div className="text-[11px] font-medium" style={{ color: 'var(--ink-2)' }}>
          {t('admin.sched.window_label', 'Zeitfenster, alle N Minuten')}
        </div>
        <div
          className="flex flex-wrap items-center gap-1.5 text-sm"
          style={{ color: 'var(--ink-2)' }}
        >
          <input
            type="time"
            value={rule.from}
            className={FIELD_CLASS}
            style={fieldStyle}
            onChange={(e) => onChange({ ...rule, from: e.target.value })}
          />
          <span>–</span>
          <input
            type="time"
            value={rule.to}
            className={FIELD_CLASS}
            style={fieldStyle}
            onChange={(e) => onChange({ ...rule, to: e.target.value })}
          />
          <span>{t('admin.sched.every', 'alle')}</span>
          <select
            value={rule.everyMinutes}
            className={FIELD_CLASS}
            style={fieldStyle}
            onChange={(e) =>
              onChange({ ...rule, everyMinutes: Number(e.target.value) as EveryMinutes })
            }
          >
            {EVERY_MINUTES.map((m) => (
              <option key={m} value={m}>
                {m} min
              </option>
            ))}
          </select>
        </div>
        <WeekdayPicker
          weekdayShort={weekdayShort}
          value={rule.days ?? null}
          onChange={(days) => onChange({ ...rule, ...(days ? { days } : { days: undefined }) })}
        />
      </div>
    );
  }

  if (rule.type === 'every_n_hours') {
    return (
      <div className="space-y-1.5">
        <div className="text-[11px] font-medium" style={{ color: 'var(--ink-2)' }}>
          {t('admin.sched.hours_label', 'Alle N Stunden')}
        </div>
        <div
          className="flex flex-wrap items-center gap-1.5 text-sm"
          style={{ color: 'var(--ink-2)' }}
        >
          <span>{t('admin.sched.every', 'alle')}</span>
          <input
            type="number"
            min={1}
            max={24}
            value={rule.n}
            className={`${FIELD_CLASS} w-16`}
            style={fieldStyle}
            onChange={(e) =>
              onChange({ ...rule, n: Math.min(24, Math.max(1, Number(e.target.value) || 1)) })
            }
          />
          <span>{t('admin.sched.hours', 'Stunden, ab')}</span>
          <input
            type="time"
            value={rule.anchor ?? '00:00'}
            className={FIELD_CLASS}
            style={fieldStyle}
            onChange={(e) => onChange({ ...rule, anchor: e.target.value })}
          />
        </div>
      </div>
    );
  }

  // weekday_at
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-medium" style={{ color: 'var(--ink-2)' }}>
        {t('admin.sched.weekday_label', 'Pro Wochentag eine Uhrzeit')}
      </div>
      <div className="space-y-1.5">
        {rule.entries.map((entry, i) => (
          <div key={`${entry.weekday}-${i}`} className="flex items-center gap-1.5">
            <select
              value={entry.weekday}
              className={FIELD_CLASS}
              style={fieldStyle}
              onChange={(e) =>
                onChange({
                  ...rule,
                  entries: rule.entries.map((x, j) =>
                    j === i ? { ...x, weekday: Number(e.target.value) as Weekday } : x,
                  ),
                })
              }
            >
              {weekdayShort.map((label, wd) => (
                <option key={label} value={wd}>
                  {label}
                </option>
              ))}
            </select>
            <input
              type="time"
              value={entry.time}
              className={FIELD_CLASS}
              style={fieldStyle}
              onChange={(e) =>
                onChange({
                  ...rule,
                  entries: rule.entries.map((x, j) =>
                    j === i ? { ...x, time: e.target.value } : x,
                  ),
                })
              }
            />
            {rule.entries.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  onChange({ ...rule, entries: rule.entries.filter((_, j) => j !== i) })
                }
                style={{ color: 'var(--ink-3)' }}
                title={t('admin.sched.remove_entry', 'Eintrag entfernen')}
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            onChange({ ...rule, entries: [...rule.entries, { weekday: 1, time: '10:00' }] })
          }
          className="text-xs px-2 py-1 rounded border"
          style={{ borderColor: 'var(--line-soft)', color: 'var(--ink-2)' }}
        >
          {t('admin.sched.add_entry', '+ Wochentag')}
        </button>
      </div>
    </div>
  );
}

/**
 * Segment-Mehrfachauswahl (Checkboxen). Null/leer = alle Segmente erlaubt.
 * Zeigt „aktuell aktive Segmente: …" bzw. „alle" als Statuszeile.
 *
 * Optional: `title` und `hint` überschreiben die internen Default-i18n-Keys —
 * nützlich, wenn die Komponente in einem anderen Kontext (z. B. Trigger-Dialog)
 * eingesetzt wird und der aufrufende Code den Titel/Hint bereits liefert.
 */
export function SegmentWhitelistEditor({
  value,
  onChange,
  title,
  hint,
}: {
  value: Segment[] | null;
  onChange: (whitelist: Segment[] | null) => void;
  title?: string;
  hint?: string;
}) {
  const { t } = useTranslation();
  const active = value ?? [];

  const toggle = (seg: Segment) => {
    const next = active.includes(seg) ? active.filter((s) => s !== seg) : [...active, seg];
    onChange(next.length === 0 ? null : next);
  };

  const activeLabel =
    active.length === 0
      ? t('admin.sched.seg_whitelist_all', 'alle')
      : active.map((s) => t(`segments.${s}`, s)).join(', ');

  const resolvedTitle =
    title ?? t('admin.sched.seg_whitelist_title', 'Segment-Filter (neue Datensätze)');
  const resolvedHint =
    hint ??
    t(
      'admin.sched.seg_whitelist_hint',
      'Leer = alle Segmente. Nur Neuanlagen werden gefiltert, bestehende Datensätze werden immer aktualisiert.',
    );

  return (
    <div
      className="rounded-lg border p-3 space-y-2"
      style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
    >
      <div className="text-[11px] font-medium" style={{ color: 'var(--ink-2)' }}>
        {resolvedTitle}
      </div>
      <p className="text-[10.5px]" style={{ color: 'var(--ink-3)' }}>
        {resolvedHint}
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {ALL_SEGMENTS.map((seg) => {
          const on = active.includes(seg);
          return (
            <label key={seg} className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(seg)}
                className="rounded"
              />
              <span style={{ color: 'var(--ink)' }}>{t(`segments.${seg}`, seg)}</span>
            </label>
          );
        })}
      </div>
      <div className="text-[10.5px]" style={{ color: 'var(--ink-3)' }}>
        {t('admin.sched.seg_whitelist_active', 'Aktuell aktive Segmente: {{active}}', {
          active: activeLabel,
        })}
      </div>
    </div>
  );
}

function WeekdayPicker({
  weekdayShort,
  value,
  onChange,
}: {
  weekdayShort: string[];
  value: Weekday[] | null;
  onChange: (days: Weekday[] | null) => void;
}) {
  const { t } = useTranslation();
  // Reihenfolge Mo..So für die Anzeige (JS getDay: 1..6,0).
  const order: Weekday[] = [1, 2, 3, 4, 5, 6, 0];
  const active = value ?? [];
  const toggle = (wd: Weekday) => {
    const next = active.includes(wd) ? active.filter((x) => x !== wd) : [...active, wd];
    onChange(next.length === 0 || next.length === 7 ? null : next);
  };
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
        {t('admin.sched.weekdays_optional', 'Wochentage (optional):')}
      </span>
      {order.map((wd) => {
        const on = value !== null && active.includes(wd);
        return (
          <button
            key={wd}
            type="button"
            onClick={() => toggle(wd)}
            className="text-[11px] px-1.5 py-0.5 rounded border"
            style={{
              borderColor: 'var(--line-soft)',
              background: on ? 'var(--primary)' : 'transparent',
              color: on ? 'var(--on-primary)' : 'var(--ink-3)',
            }}
          >
            {weekdayShort[wd]}
          </button>
        );
      })}
    </div>
  );
}
